import type { ConsentPayload } from '@models/consent';

export type AuthMethod = 'TOTP' | 'RecoveryCode';

export type UserGroup = 'patient' |
  'provider' |
  'providerUser' |
  'medicalSupport' |
  'medicalSupportManager' |
  'customerSupport' |
  'customerSupportManager';

export interface User {
  id: number;
  email: string;
  password: string;
  phone: string;
  fullName: string
}

export interface UserUpdateMetaPayload {
  fbc?: string | null;
  fbp?: string | null;
  campaign?: string | null;
  efTransactionId?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  marketingConsent?: boolean | null;
  consent?: ConsentPayload;
}

export const MARKETING_CONSENT_LABEL = 'Yes, by continuing I agree to receive personalized marketing emails at the address you provide with updates, resources, and offers. This is separate from required medical communications.';

export interface UserRegisterPayload {
  email: string;
  password: string;
  verificationCode?: string;
  quizHandle?: string;
  name?: string;
  firstName: string;
  lastName: string;
  siteHandle?: string;
  campaign?: string;
  fbc?: string;
  efTransactionId?: string;
  utmSource?: string | null;
  utmMedium?: string | null;
  marketingConsent?: boolean | null;
  consent?: ConsentPayload;
}

export interface UserLoginPayload {
  loginName: string;
  password: string;
}

export interface ResetPasswordPayload {
  loginName: string;
}

export interface SetPasswordPayload {
  code: string;
  id: string;
  newPassword: string;
}

export interface UserLoginResponse {
  returnUrl: string;
  csrfTokenValue: string;
}

export interface UserLoginTOTPResponse {
  authMethod: string;
  otherMethods: string[];
  authForm: string;
  returnUrl: string;
  headHtml: string;
  bodyHtml: string;
}

export interface UserTOTPVerifyResponse {
  message: string;
  redirect?: string | null;
}

export interface SessionInfoResponse {
  email: string;
  username: string;
  isGuest: boolean;
  isPatient: boolean;
  isProvider: boolean;
  isProviderUser: boolean;
  csrfTokenName: string;
  csrfTokenValue: string;
  timeout: number;
  id: number;
  uid: string;
  groups: UserGroup[];
}

export interface UserRegisterResponse {
  csrfTokenValue: string;
}

export interface ResetPasswordResponse {
  message: string;
  errors?: string[];
}

export interface SetPasswordResponse {
  message: string;
  errors?: string[];
}

export interface ProfilePayload {
  user: {
    id?: number;
    firstName: string;
    lastName: string;
    middleName: string;
    password?: string;
    phone?: string;
    isSms?: boolean
  };
  profession: string,
  licenses: Array<any>,
  avatar: string,
  signature: string,
  avatarFile?: string,
  signatureFile?: string,
  shipping: {
    name: string
    phone?: string
    address: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    }
  }
}

export interface ProfilePaymentMethod {
  id: string
  name: string
  card?: {
    brand: string;
    expMonth: string;
    expYear: string;
    last4: string;
  }
  link?: {
    email: string
  }
  cashapp?: {}
}

export interface VerifyEmailResponse {
  result: 'deliverable' | 'undeliverable' | 'do_not_send' | 'catch_all' | 'unknown';
  risK: 'high' | 'medium' | 'low' | 'unknown';
}

export interface ProfileResponse {
  user: {
    id: number;
    email: string;
    isSms: boolean;
    phone: string;
    firstName: string;
    lastName: string;
    middleName: string;
    dob: number;
    gender: string | null;
    country: string | null;
    addresses: {
      billingAddress: ProfileAddress | null;
      shippingAddress: ProfileAddress | null;
    };
    document?: string | null;
    insuranceCardFront?: string | null;
    insuranceCardBack?: string | null;
    idPicture?: string | null;
    consentEmailGeneralMarketing?: boolean;
    consentEmailPersonalizedQuiz?: boolean;
    consentEmailPersonalizedHealth?: boolean;
    consentSmsMarketing?: boolean;
    consentSmsTransactional?: boolean;
    smsStop?: boolean;
  };
  profession: string,
  licenses: Array<any>,
  avatar: string,
  isTwoWayState: boolean,
  signature: string,
  shipping: {
    name: string
    phone?: string
    address: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    }
  },
  setupIntent: {
    clientSecret: string;
  };
}

export interface ProfileAddress {
  id: number;
  countryCode: string;
  administrativeArea: string;
  locality: string;
  postalCode: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  phone: string;
}

export interface GetPaymentMethodsResponse {
  paymentMethods: PaymentMethod[];
}

export interface PaymentMethod {
  externalRef: string;
  card: {
    type: 'visa' | 'mastercard' | 'american_express' | 'discover' | 'jcb';
    ending: string;
    expirationDate: string;
    default: boolean;
  };
}
